import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spin } from 'antd-v5';
import { PlusOutlined } from '@ant-design/icons';
import InquiryHeader from './components/InquiryHeader';
import StorySection from './components/StorySection';
import TagsSection from './components/TagsSection';
import RequestedProducts from './components/RequestedProducts';
import ClosedInquiries from './components/ClosedInquiries';
import AddRolexInquiryModal from '../../../../../componentsV2/Rolex/AddRolexInquiryModal';
import { getAllMerchantTags } from '../../../../../services/clients';
import { formatInquiryDate } from '../../../Rolex/utils';
import useVendorByKey from '../../../../../hooks/query/useVendorByKey';
import useClientInquiries from '../../../../../hooks/query/useClientInquiries';
import './styles.scss';

const Rolex = ({ client, onShareProducts }) => {
  const { vendorId: rolexVendorId } = useVendorByKey('rolex');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewingInquiry, setViewingInquiry] = useState(null);
  const [tagLookup, setTagLookup] = useState([]);

  const clientId = client?.client_id || client?.id;
  const createInitialClient = useMemo(
    () => (clientId ? { id: clientId, name: client?.name } : null),
    [clientId, client?.name],
  );

  const { data: inquiriesResponse, isLoading, refetch } = useClientInquiries(clientId);
  const rawActive = inquiriesResponse?.data?.[0] || null;
  const rawClosed = inquiriesResponse?.closedInquiries || [];
  const loading = isLoading;

  useEffect(() => {
    getAllMerchantTags({ loadingStatusOff: true })
      .then(merchantTags => setTagLookup(merchantTags || []))
      .catch(() => {});
  }, []);

  // Resolve tag names/colors at render time so changes to tagLookup
  // don't trigger a refetch
  const activeInquiry = useMemo(() => {
    const inq = rawActive;
    if (!inq) return null;
    let resolvedTags;
    if (inq.tags && inq.tags.length > 0 && typeof inq.tags[0] === 'object') {
      resolvedTags = inq.tags
        .map(t => ({
          name: t.tagName || t.name,
          color: t.color || tagLookup.find(mt => mt.id === t.tagId)?.color || '#818181',
        }))
        .filter(t => t.name);
    } else {
      resolvedTags = (inq.tagIds || [])
        .map(id => {
          const lookup = tagLookup.find(t => t.id === id);
          return lookup ? { name: lookup.name, color: lookup.color || '#818181' } : null;
        })
        .filter(Boolean);
    }
    return {
      ...inq,
      createdDate: formatInquiryDate(inq.inquiryDate),
      createdByName: inq.createdByName || '',
      tags: resolvedTags,
      requestedProducts: inq.requestedProducts || [],
    };
  }, [rawActive, tagLookup]);

  const closedInquiries = useMemo(
    () =>
      rawClosed.map(inq => ({
        ...inq,
        createdDate: formatInquiryDate(inq.inquiryDate),
        closedDate: formatInquiryDate(inq.lastModifiedTs),
        associateName: inq.associateName || '',
      })),
    [rawClosed],
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  const editInquiry = activeInquiry
    ? {
        ...activeInquiry,
        clientId,
        clientName: client?.name,
        associate: activeInquiry.associateName,
        models: activeInquiry.requestedProducts?.map(p => p.mpn) || [],
        inquiryDate: activeInquiry.inquiryDate,
      }
    : null;

  return (
    <div
      className="flex-col-left"
      style={{
        alignItems: 'stretch',
        maxHeight: '100%',
        gap: '36px',
        overflowX: 'hidden',
        paddingLeft: '26px',
        paddingRight: '26px',
      }}
    >
      {activeInquiry ? (
        <>
          <InquiryHeader
            createdDate={activeInquiry.createdDate}
            createdBy={activeInquiry.createdByName}
            assignedTo={activeInquiry.associateName}
            status={activeInquiry.status}
            onEdit={() => setShowEditModal(true)}
          />
          <StorySection story={activeInquiry.story} />
          <TagsSection tags={activeInquiry.tags} />
          <RequestedProducts products={activeInquiry.requestedProducts} onShare={onShareProducts} />
        </>
      ) : (
        <div className="qv-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ color: 'rgba(0,0,0,0.5)', fontSize: 14 }}>No open Rolex inquiry for this client</span>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreateModal(true)}>
            Create Inquiry
          </Button>
        </div>
      )}
      <ClosedInquiries
        inquiries={closedInquiries}
        onView={inquiry =>
          setViewingInquiry({
            ...inquiry,
            clientId,
            clientName: client?.name,
            associate: inquiry.associateName,
            models: inquiry.requestedProducts?.map(p => p.mpn) || [],
          })
        }
      />

      {editInquiry && (
        <AddRolexInquiryModal
          open={showEditModal}
          onCancel={() => setShowEditModal(false)}
          onSave={() => {
            setShowEditModal(false);
            refetch();
          }}
          inquiry={editInquiry}
          vendorId={rolexVendorId}
        />
      )}

      <AddRolexInquiryModal
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        onSave={() => {
          setShowCreateModal(false);
          refetch();
        }}
        initialClient={createInitialClient}
        vendorId={rolexVendorId}
      />

      {viewingInquiry && (
        <AddRolexInquiryModal
          open
          onCancel={() => setViewingInquiry(null)}
          onSave={() => setViewingInquiry(null)}
          inquiry={viewingInquiry}
          vendorId={rolexVendorId}
        />
      )}
    </div>
  );
};

export default Rolex;
